import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ContentChildren, EventEmitter, input, Input, output, Output, QueryList, TrackByFunction, ViewChild } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import {  MatTableDataSource } from '@angular/material/table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { CellTemplateDirective, RowDetailTemplateDirective } from '../../directives';
import { DataTableColumn, SearchParamsInterface } from '../../models';
import { DataTableTemplatesEnum } from '../../enums';
import { ResponseDto } from '../../../../../dto';
import { filter } from 'rxjs/operators';
import { MatMenuPanel } from '@angular/material/menu';

@UntilDestroy()
@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class DataTableComponent<T> {
    @Input() set columns(columns: DataTableColumn[]) {
        this.innerColumnsSubject.next(this.setColumns(columns));
    }
    get columns() {
        return this.innerColumnsSubject.getValue();
    }
    @Input() set clearSelection(value: symbol) {
        if (value) {
            this.onClearSelection();
        }
    }
    @Input() set data(value: T[]) {
        this.dataSubject.next(value);
    }

    @Input() set selectedDataRows(rows: T[]) {
        if (rows) {
            setTimeout(() => {
                this.selection.clear();
                this.selection.select(...rows);
                this.cdr.detectChanges();
            })
        }
    }

    @Input() selectAllMenu: MatMenuPanel;

    /**
     * Use only in simple cases without complex logic
     */
    @Input() set selectionRowDisabledCondition(value: (row: T) => boolean) {
        this.selectionRowDisabledConditionSubject.next(value);
    };
    get selectionRowDisabledCondition() {
        return this.selectionRowDisabledConditionSubject.getValue();
    }
    @Input() set selectionRowDisabledTooltipFn(fn: (row: T) => string) {
        this.selectionRowDisabledTooltipFnSubject.next(fn);
    }
    get selectionRowDisabledTooltipFn(): (row: T) => string {
        return this.selectionRowDisabledTooltipFnSubject.getValue();
    }

    /**
     * Store functionality Not ready yet
     */
    @Input() store: { getData: (searchParams?: SearchParamsInterface) => Observable<ResponseDto<T>> };
    @Input() stickyHeader: boolean = true;
    /**
     * Defines sort direction on first time click on sorting header
     */
    @Input() sortStart: SortDirection = 'desc';
    /**
     * Field ID for initial table sort
     */
    @Input() sortActive: string;
    /**
     * Sort direction for sortActive field
     */
    @Input() sortDirection: SortDirection;
    @Input() customSorting: boolean;
    @Input() trackBy: TrackByFunction<T>;
    @Input() noDataText: string = "No Records Found";
    @Input() isLoadingText: string = "Loading results...";
    @Input() isLoading: boolean;
    @Input() isLoadMore: boolean;
    @Input() set isSelection(value: boolean) {
        this.innerIsSelectionSubject.next(value);
    }
    get isSelection() {
        return this.innerIsSelectionSubject.getValue();
    }
    @Input() layoutFixed: boolean = true;
    @Input() hideHeader: boolean = false;
    @Input() smallRowHeight: boolean = false;
    @Input() selectionRowDisabledTooltip: string;
    @Input() showDivider: (index: number, row: T) => boolean = () => false;
    @Output() selectionChanged: EventEmitter<T[]> = new EventEmitter<T[]>();
    @Output() sortingChanged: EventEmitter<Sort> = new EventEmitter<Sort>();

    @ContentChildren(CellTemplateDirective, {
        emitDistinctChangesOnly: true,
    }) cellTemplates: QueryList<CellTemplateDirective<T>>;

    @ContentChild(RowDetailTemplateDirective, { static: false }) rowDetailTemplate: RowDetailTemplateDirective<T>;

    @ViewChild(MatSort) sort: MatSort;

    columnNames: string[];
    dataSource: MatTableDataSource<T>;
    dataWithoutDisabledSelections: T[] = [];
    disabledRowsMap: Map<T, boolean> = new Map();
    disabledTooltipsMap: Map<T, string> = new Map();
    selectedRowsSet: Set<T> = new Set<T>();
    selectedRowsArray: T[];

    readonly expandedRows: Set<T> = new Set();
    readonly dateFormat: string = 'MMM dd, yyyy';
    readonly dataTableTemplatesEnum: typeof DataTableTemplatesEnum = DataTableTemplatesEnum;

    private readonly selection = new SelectionModel<T>(true, []);
    private readonly innerColumnsSubject: BehaviorSubject<DataTableColumn[]> = new BehaviorSubject<DataTableColumn[]>([]);
    private readonly innerIsSelectionSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly columnDefaults: Partial<DataTableColumn> = {
        allowSorting: true,
    }
    private readonly dataSubject = new BehaviorSubject(undefined);
    private readonly selectionRowDisabledConditionSubject = new BehaviorSubject(undefined);
    private readonly selectionRowDisabledTooltipFnSubject = new BehaviorSubject<(row: T) => string>(null);

    constructor(private readonly cdr: ChangeDetectorRef) {
        this.initDataSource();
        this.initColumnsAndSelectionObservables();
        this.initDataObservables();
    }

    ngAfterViewInit(): void {
        if (!this.customSorting) {
            this.dataSource.sort = this.sort;
        }
    }

    onToggleMasterSelection(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.selection.select(...this.dataWithoutDisabledSelections);
        }

        this.selectionChanged.emit(this.selection.selected);
    }

    onClearSelection(): void {
        this.selection.clear();
        this.selectionChanged.emit(this.selection.selected);
    }

    onToggleSelection(row: T): void {
        this.selection.toggle(row);
        this.selectionChanged.emit(this.selection.selected);
    }

    onToggleExpansion(row: T): void {
        if (this.rowDetailTemplate) {
            if (this.expandedRows.has(row)) {
                this.expandedRows.delete(row);
            } else {
                this.expandedRows.add(row);
            }
        }
    }

    onSortChange(sort: Sort): void {
        this.sortingChanged.emit(sort);
    }

    private initDataSource(): void {
        this.dataSource = new MatTableDataSource();
        this.dataSource.sortingDataAccessor = (data: T, sortHeaderId: string): string => {
            if (typeof data[sortHeaderId] === 'string') {
                return data[sortHeaderId].toLocaleLowerCase();
            }

            return data[sortHeaderId];
        };
    }

    private setColumns(columns: DataTableColumn[]): DataTableColumn[] {
        return columns?.map((column: DataTableColumn) => ({
            ...this.columnDefaults,
            ...column,
        }))
    }

    private initColumnsAndSelectionObservables(): void {
        combineLatest([
            this.innerColumnsSubject,
            this.innerIsSelectionSubject,
        ]).pipe(untilDestroyed(this)).subscribe(([columns, isSelection]: [DataTableColumn[], boolean]) => {
                this.columnNames = columns?.map((column: DataTableColumn) => column.dataField);

                if (isSelection) {
                    this.columnNames.unshift('selection');
                }
            })
    }

    private initDataObservables(): void {
        this.selection.changed.asObservable().pipe(untilDestroyed(this)).subscribe(() => {
            this.selectedRowsSet = new Set(this.selection.selected);
            this.selectedRowsArray = Array.from(this.selectedRowsSet);
        })

        combineLatest([
            this.dataSubject.pipe(filter(Boolean)),
            this.selectionRowDisabledConditionSubject,
            this.selectionRowDisabledTooltipFnSubject
        ]).subscribe(([value, disabledFn, tooltipFn]: [T[], (row: T) => boolean, (row: T) => string]) => {
            if (value) {
                this.dataSource.data = value;
                this.dataWithoutDisabledSelections = disabledFn ? value.filter((row: T) => !disabledFn(row)) : value;

                // Update disabled rows and tooltip caches
                this.disabledRowsMap.clear();
                this.disabledTooltipsMap.clear();

                if (disabledFn) {
                    value.forEach(row => {
                        const isDisabled = disabledFn(row);
                        this.disabledRowsMap.set(row, isDisabled);

                        if (isDisabled) {
                            let tooltipText: string = '';
                            if (tooltipFn) {
                                tooltipText = tooltipFn(row);
                            } else if (this.selectionRowDisabledTooltip) {
                                tooltipText = this.selectionRowDisabledTooltip;
                            }

                            if (tooltipText) {
                                this.disabledTooltipsMap.set(row, tooltipText);
                            }
                        }
                    });
                }
            }

            if (!value || !value?.length) {
                this.onClearSelection();
            }
        });
    }

    private isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataWithoutDisabledSelections.length;
        return numSelected === numRows;
    }
}
